import { useEffect, useRef } from 'react';
import { BASE_URL } from '../api/helpers';
import { getToken } from '../api/auth';

type WebSocketEventHandler = (eventType: string, data: any) => void;

interface UseWebSocketOptions {
  onEvent: WebSocketEventHandler;
  /**
   * Called every time the socket opens, including each reconnect. A reconnect
   * means events may have been missed in between (the hub does not replay), so
   * this is the moment for a consumer to resync whatever it derives from them.
   */
  onOpen?: () => void;
  reconnectDelay?: number;
  endpoint?: string;
}

/**
 * The hub authenticates the handshake and fans per-user events out only to
 * sockets that carry the user's session (backend webhook.HandleWebSocket).
 * Browsers cannot set headers on a WebSocket upgrade, so the token travels as
 * the `token` query param.
 */
export function socketUrl(endpoint: string, token: string): string {
  // Derive the WebSocket URL from the current page origin (same-origin via Caddy).
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = new URL(`${protocol}//${window.location.host}${BASE_URL}${endpoint}`);
  url.searchParams.set('token', token);
  return url.toString();
}

export function useWebSocket({
  onEvent,
  onOpen,
  reconnectDelay = 3000,
  endpoint = '/webhook'
}: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();

  // Keep the latest handlers in refs so a changing identity (callers routinely
  // pass inline arrows) never tears down and reopens the socket.
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  // Read per render: a login or logout re-renders every consumer through the
  // auth context, and the effect below reconnects with the new session.
  const token = getToken();

  useEffect(() => {
    // Without a session the hub refuses the handshake (401); opening a socket
    // would only loop through reconnects.
    if (!token) return undefined;

    let intentionallyClosed = false;

    const connect = () => {
      const ws = new WebSocket(socketUrl(endpoint, token));
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        onOpenRef.current?.();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const eventType = data.event || data.type;
          console.log('WebSocket event:', eventType, data);
          onEventRef.current(eventType, data);
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        if (intentionallyClosed) return;
        console.log('WebSocket disconnected, reconnecting in', reconnectDelay, 'ms');
        reconnectTimeoutRef.current = setTimeout(connect, reconnectDelay);
      };
    };

    connect();

    return () => {
      intentionallyClosed = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        // Neutralize onclose first so the async close event cannot schedule a
        // stale reconnect after unmount.
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [reconnectDelay, endpoint, token]);

  return wsRef;
}
